/**
 * New Chat Modal
 *
 * SlideoverModal to start a new 1:1 chat:
 *  - Search users with debounce
 *  - Infinite scroll for pagination
 *  - Click a user to start chatting
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import TestImage from "../../../assets/images/TestImages/user.png";
import Button from "../../../base-components/Button";
import Lucide from "src/base-components/Lucide";
import SlideoverModal from "../../SlideoverModal";
import { ChatUser } from "../index";

interface NewChatDropdownProps {
  onSelectUser: (user: { id: string; name: string }) => void;
  chatUsers: ChatUser[];
  currentUserId: string;
  loading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onSearch?: (query: string) => void;
}

const NewChatDropdown: React.FC<NewChatDropdownProps> = ({
  onSelectUser,
  chatUsers,
  currentUserId,
  loading = false,
  hasMore = false,
  onLoadMore,
  onSearch,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Filter out current user
  const availableUsers = chatUsers.filter(
    (u) => String(u.id) !== currentUserId
  );

  // Infinite scroll — load more when near bottom
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || loading || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      onLoadMore?.();
    }
  }, [loading, hasMore, onLoadMore]);

  // Debounced search
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch?.(value), 400);
  };

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      onSearch?.("");
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [isOpen]);

  const handleSelectUser = (user: ChatUser) => {
    const fullName = `${user.name}${user.last_name ? " " + user.last_name : ""}`;
    onSelectUser({ id: String(user.id), name: fullName });
    setIsOpen(false);
  };

  return (
    <div>
      <Button
        variant="outline-primary"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1 h-[31px] border border-[#C2CDDB]"
      >
        <Lucide icon="Plus" className="w-4 h-4" />
        New Chat
      </Button>

      <SlideoverModal
        size="md"
        visible={isOpen}
        onClose={() => setIsOpen(false)}
        headerLeft={
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[linear-gradient(135deg,#0F3460_0%,#0A2342_100%)] flex items-center justify-center">
              <Lucide icon="MessageCircle" className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-[16px] text-[#1D293D] font-bold">New Chat</h2>
          </div>
        }
      >
        <div className="flex flex-col h-full">
          {/* Search */}
          <div className="mb-3">
            <div className="relative">
              <Lucide
                icon="Search"
                className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-0 focus:border-slate-200 bg-slate-50"
                autoFocus
              />
            </div>
          </div>

          {/* User List */}
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto space-y-1 rounded-lg"
            onScroll={handleScroll}
          >
            {availableUsers.length === 0 && !loading ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Lucide icon="UserX" className="w-10 h-10 mb-2" />
                <p className="text-sm">No users found</p>
              </div>
            ) : (
              availableUsers.map((user) => (
                <div
                  key={user.id}
                  onClick={() => handleSelectUser(user)}
                  className="flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition hover:bg-slate-50"
                >
                  <img
                    src={TestImage}
                    className="w-9 h-9 rounded-full object-cover"
                    alt={user.name}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {user.name}
                      {user.last_name ? ` ${user.last_name}` : ""}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{user.email}</p>
                  </div>
                  <Lucide icon="MessageSquare" className="w-4 h-4 text-primary flex-shrink-0" />
                </div>
              ))
            )}
            {loading && (
              <div className="flex items-center justify-center py-3">
                <Lucide icon="Loader" className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            )}
          </div>
        </div>
      </SlideoverModal>
    </div>
  );
};

export default NewChatDropdown;